import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface MetricRow {
  readonly analyticsId: string;
  readonly metricCode: string;
  readonly metricCategory: string;
  readonly metricName: string;
  readonly metricValue: string;
  readonly metricUnit: string;
  readonly periodType: string;
  readonly trendDirection: string;
  readonly computedAt: string;
}

const CATEGORIES = ['', 'THREAT', 'RISK', 'COMPLIANCE', 'INCIDENT', 'VULNERABILITY', 'USER_RISK', 'SECURITY_HEALTH', 'PRIVACY', 'AWARENESS', 'THIRD_PARTY'];
const PERIODS = ['', 'HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'SNAPSHOT'];

/**
 * M30 — Security Analytics Dashboard (§21 / §31). Time-series of computed
 * security metrics across every domain (threat, risk, compliance, incident,
 * vulnerability, privacy, awareness, third-party) with their value, unit,
 * aggregation period and trend direction, feeding the executive posture view.
 */
export function M30SecurityAnalyticsPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<MetricRow[]>([]);
  const [metricCategory, setMetricCategory] = useState('');
  const [periodType, setPeriodType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (metricCategory) params.set('metricCategory', metricCategory);
    if (periodType) params.set('periodType', periodType);
    const qs = params.toString();
    apiRequest<{ data: MetricRow[] }>(`/v1/security/analytics/metrics${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, metricCategory, periodType]);

  return (
    <main aria-label="security-analytics">
      <h1>{t('m30.analytics.title')}</h1>
      <div className="filters">
        <label>
          {t('m30.analytics.filterCategory')}
          <select aria-label="filter-metric-category" value={metricCategory} onChange={(e) => setMetricCategory(e.target.value)}>
            {CATEGORIES.map((c) => <option key={c || 'all'} value={c}>{c || t('m30.analytics.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m30.analytics.filterPeriod')}
          <select aria-label="filter-period-type" value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
            {PERIODS.map((p) => <option key={p || 'all'} value={p}>{p || t('m30.analytics.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m30.analytics.loading')}</p> : (
        <>
          <p aria-label="metric-count">{t('m30.analytics.count', { count: rows.length })}</p>
          <table aria-label="metric-table">
            <thead><tr><th>{t('m30.analytics.thCode')}</th><th>{t('m30.analytics.thCategory')}</th><th>{t('m30.analytics.thName')}</th><th>{t('m30.analytics.thValue')}</th><th>{t('m30.analytics.thUnit')}</th><th>{t('m30.analytics.thPeriod')}</th><th>{t('m30.analytics.thTrend')}</th></tr></thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.analyticsId}>
                  <td>{m.metricCode}</td><td>{m.metricCategory}</td><td>{m.metricName}</td>
                  <td>{m.metricValue}</td><td>{m.metricUnit}</td><td>{m.periodType}</td><td>{m.trendDirection}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
